//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import React, { useEffect, useRef, useState } from 'react';
import { ControlBarVisibility, DefaultThemeControl, OmakasePlayer, PlayerChromingTheme } from '@byomakase/omakase-player';
import '@byomakase/omakase-player/dist/style.css';
import useComponentOverride from '@/components/_hooks/useComponentOverride';
import PolarisIcon from '../PolarisIcon';
import { Grid, useTheme } from '@mui/material';

export type VideoPlayerProps = {
  url: string | null | undefined;
  poster?: string;
  autoplay?: boolean;
  id?: string;
  stopPropagation?: boolean;
};

const detectProtocol = (url: string) => {
  if (!url) return undefined;

  const lower = url.toLowerCase();

  if (lower.includes(".m3u8")) {
    return "hls";
  }

  if (lower.includes(".mp3") || lower.includes(".aac") || lower.includes(".ogg")) {
    return "audio";
  }

  return "native";
}

const isTypingTarget = (target: EventTarget | null) => {
  const element = target instanceof HTMLElement ? target : null;
  if (!element) return false;

  const typingElement = element.closest('textarea, input, [contenteditable="true"], [role="textbox"], [role="combobox"]');
  if (!typingElement) return false;

  if (typingElement instanceof HTMLInputElement) {
    const nonTextInputTypes = new Set([
      'button',
      'checkbox',
      'color',
      'file',
      'hidden',
      'image',
      'radio',
      'range',
      'reset',
      'submit',
    ]);

    return !nonTextInputTypes.has((typingElement.type || 'text').toLowerCase());
  }

  return true;
};

const VideoPlayer = ({ url, poster, autoplay = true, id = 'player', stopPropagation = false }: VideoPlayerProps) => {
  const [hasError, setHasError] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const playerRef = useRef<OmakasePlayer | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const theme = useTheme();
  const resolvedPoster = poster || theme.preview_placeholder;


  useEffect(() => {
    if (!containerRef.current || !url) return;

    // Initialize player
    const player = new OmakasePlayer({
      playerHTMLElementId: id,
      playerChroming: {
        theme: PlayerChromingTheme.Default,
        themeConfig: {
          controlBarVisibility: ControlBarVisibility.Enabled,
          controlBar: [
            DefaultThemeControl.Play,
            DefaultThemeControl.Scrubber,
            DefaultThemeControl.Volume,
            DefaultThemeControl.Fullscreen,
            DefaultThemeControl.Scrubber,
            DefaultThemeControl.Trackselector,
            DefaultThemeControl.Detach,
            DefaultThemeControl.PlaybackRate,
          ],
          playbackRates: [0.25, 0.5, 1, 1.5, 2],
        },
      },
    });
    const protocol = detectProtocol(url);

    playerRef.current = player;

    // Load a video
    player.loadVideo(url, {
      poster: poster || theme.preview_placeholder,
      protocol,
    }).subscribe({
      next: () => {
        if (autoplay) {
          player.video.play();
        }
      },
      error: () => setHasError(true),
    });

    // Cleanup on unmount
    return () => {
      player.destroy();
    };
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  const onPlayVideo = () => {
    setIsPlaying(true);
    const root = document?.querySelector('.omakase-player-wrapper media-theme');
    const loadingElement = root?.shadowRoot?.querySelector<HTMLDivElement>('.omakase-button-loading');

    if (loadingElement) {
      loadingElement.style.opacity = '0';
    }
  };

  useEffect(() => {
    const videoElement = containerRef.current?.querySelector('video') as HTMLVideoElement | undefined;
    const onPauseVideo = () => setIsPlaying(false);

    if (!videoElementRef.current && videoElement) {
      videoElementRef.current = videoElement;
      videoElement.addEventListener('playing', onPlayVideo);
      videoElement.addEventListener('pause', onPauseVideo);

      return () => {
        videoElement.removeEventListener('playing', onPlayVideo);
        videoElement.removeEventListener('pause', onPauseVideo);
      };
    }
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.key === ' ') {
        const target = e.target || document.activeElement;
        if (isTypingTarget(target)) {
          return;
        }

        e.preventDefault();
        const video = playerRef.current?.video;
        if (video) {
          if (video.isPaused()) {
            video.play();
          } else {
            video.pause();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const toggleFullscreen = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    playerRef.current?.video?.toggleFullscreen();
  };

  return (
    <PlayerContainer style={{ position: 'relative' }} container>
      {!hasError && !isPlaying && resolvedPoster && <PosterOverlay src={resolvedPoster} alt="Video poster" />}
      <StyledVideoPlayerDiv ref={containerRef} id={id} onClick={handleClick} onDoubleClick={toggleFullscreen}>
        {hasError && (
          <StyledPlayerErrorDiv>
            <PolarisIcon icon="error" height={20} style={{ marginRight: 10 }} />
            <div>Unable to load video</div>
          </StyledPlayerErrorDiv>
        )}
      </StyledVideoPlayerDiv>
    </PlayerContainer>
  );
};

export default (props: VideoPlayerProps) => useComponentOverride<VideoPlayerProps>(VideoPlayer, 'videoPlayer', props);

const PlayerContainer = styled(Grid)`
  &:hover {
    .controls {
      opacity: 1;
      visibility: visible;
    }
  }
`;

const StyledVideoPlayerDiv = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  &::before {
    content: " ";
    display: block;
    width: 100%;
    padding-bottom: ${(9 / 16) * 100}%;
  }

  &.omakase-player .omakase-player-wrapper {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    aspect-ratio: 16/9 !important;

    .omakase-watermark-wrapper, .omakase-button-loading {
      display: none !important;
    }

    video[slot="media"] {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }
`;

const StyledPlayerErrorDiv = styled.div`
  position: absolute;
  display: flex;
  line-height: 1.2;
  font-size: 1.2rem;
  top: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  z-index: 99;
  white-space: nowrap;
`;

const PosterOverlay = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => theme.spacing(3)};
  box-sizing: border-box;
  object-fit: contain;
  background: ${({ theme }) => theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
    : 'linear-gradient(135deg, #1a1d25 0%, #111216 100%)'
  };
  z-index: -1;
  pointer-events: none;
`;
